import { MouseEvent, ReactNode, useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import AddIcon from "@atlaskit/icon/core/add";
import AtlassianIntelligenceIcon from "@atlaskit/icon/core/atlassian-intelligence";
import AutomationIcon from "@atlaskit/icon/core/automation";
import CheckCircleIcon from "@atlaskit/icon/core/check-circle";
import {
    ConfluenceIcon,
    JiraLogo,
    LoomIcon,
    RovoIcon
} from "@atlaskit/logo-next";
import { media } from "@atlaskit/primitives";
import { JiraBackground } from "@atlassian/product-wireframes";

import { useAnalyticsClient } from "@/analytics/wac-analytics";
import { Button } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";
import { useLocation } from "@/utils/location-provider";

import { LayoutVersion, Modal } from "../layouts";

export const JiraPlus = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    useEffect(() => {
        if (!analyticsClient) {
            return; // be sure analyticsClient is available before firing event
        }
        createAnalyticsEvent({
            action: "viewed",
            actionSubject: "component",
            actionSubjectId: "jiraPlusModal",
            attributes: {
                source: "jiraPlusModal"
            }
        }).fire();
    }, [analyticsClient, createAnalyticsEvent]);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const editionIntent = searchParams.get("editionIntent");

    const jiraSignupHref = useLocalizedHref(
        `/try/cloud/signup${
            editionIntent ? `?editionIntent=${editionIntent}` : ""
        }`
    );
    const twcSignupHref = useLocalizedHref(
        `/try/cloud/signup?bundle=teamwork-collection&edition=premium&touchpoint=jira-plus`
    );

    return (
        <JiraBackground>
            <Modal version={LayoutVersion.V3}>
                <section css={modalContentCss}>
                    <article css={[choiceCss, jiraChoiceCss]}>
                        <div css={logosCss}>
                            <JiraLogo
                                size="medium"
                                appearance="brand"
                                shouldUseNewLogoDesign
                            />
                        </div>
                        <h1 css={headingCss}>
                            <FormattedMessage
                                id="jiraPlus.jiraHeading"
                                defaultMessage="Get started with Jira only"
                            />
                        </h1>
                        <p css={descriptionCss}>
                            <FormattedMessage
                                id="jiraPlus.jiraDescription"
                                defaultMessage="Get AI-powered project management for all teams."
                            />
                        </p>
                        <ul css={listCss}>
                            <li css={[listItemCss, jiraListItemCss]}>
                                <CheckCircleIcon label="" />
                                <FormattedMessage
                                    id="jiraPlus.planAndTrack"
                                    defaultMessage="Plan and track your work"
                                />
                            </li>
                            <li css={[listItemCss, jiraListItemCss]}>
                                <AtlassianIntelligenceIcon label="" />
                                <FormattedMessage
                                    id="jiraPlus.aiPoweredProjectsAndInsights"
                                    defaultMessage="Get AI-powered projects and insights"
                                />
                            </li>
                            <li css={[listItemCss, jiraListItemCss]}>
                                <AutomationIcon label="" />
                                <FormattedMessage
                                    id="jiraPlus.automateTasks"
                                    defaultMessage="Automate tasks to keep work moving smoothly"
                                />
                            </li>
                        </ul>
                        <Button
                            css={[buttonCss, jiraButtonCss]}
                            href={jiraSignupHref}
                            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                                e.preventDefault(); // wait for analytics event to fire before navigating
                                createAnalyticsEvent({
                                    action: "clicked",
                                    actionSubject: "button",
                                    actionSubjectId: "getJiraOnly",
                                    attributes: {
                                        source: "jiraPlusModal"
                                    }
                                }).fire();
                                window.location.assign(jiraSignupHref);
                            }}
                        >
                            <FormattedMessage
                                id="jiraPlus.jiraButtonText"
                                defaultMessage="Get Jira only"
                            />
                        </Button>
                    </article>
                    <div css={separatorCss} />
                    <article css={[choiceCss, twcChoiceCss]}>
                        <div css={[logosCss, twcLogosCss]}>
                            <JiraLogo
                                size="medium"
                                appearance="brand"
                                shouldUseNewLogoDesign
                            />
                            <AddIcon label="" />
                            <div css={iconsCss}>
                                <ConfluenceIcon
                                    appearance="brand"
                                    shouldUseNewLogoDesign
                                />
                                <LoomIcon
                                    appearance="brand"
                                    shouldUseNewLogoDesign
                                />
                                <RovoIcon
                                    appearance="brand"
                                    shouldUseNewLogoDesign
                                />
                            </div>
                        </div>
                        <h1 css={headingCss}>
                            <FormattedMessage
                                id="jiraPlus.twcHeading"
                                defaultMessage="Or work <em>3x faster</em> with teamwork apps"
                                values={{
                                    em: headingEmphasis
                                }}
                            />
                        </h1>
                        <p css={descriptionCss}>
                            <FormattedMessage
                                id="jiraPlus.twcDescription"
                                defaultMessage="Boost productivity by adding apps that supercharge teamwork."
                            />
                        </p>
                        <p css={listIntroCss}>
                            <FormattedMessage
                                id="jiraPlus.twcListIntro"
                                defaultMessage="Get everything from Jira, plus:"
                            />
                        </p>
                        <ul css={listCss}>
                            <li css={listItemCss}>
                                <ConfluenceIcon
                                    appearance="brand"
                                    shouldUseNewLogoDesign
                                    size="xxsmall"
                                />
                                <span>
                                    <FormattedMessage
                                        id="jiraPlus.bundleJiraWithConfluence"
                                        defaultMessage="Bundle Jira with <em>Confluence</em>, so all your work stays in one place"
                                        values={{ em: listEmphasis }}
                                    />
                                </span>
                            </li>
                            <li css={listItemCss}>
                                <LoomIcon
                                    appearance="brand"
                                    shouldUseNewLogoDesign
                                    size="xxsmall"
                                />
                                <span>
                                    <FormattedMessage
                                        id="jiraPlus.saveTimeWithLoom"
                                        defaultMessage="Save time with <em>Loom’s</em> AI-generated meeting notes"
                                        values={{ em: listEmphasis }}
                                    />
                                </span>
                            </li>
                            <li css={listItemCss}>
                                <RovoIcon
                                    appearance="brand"
                                    shouldUseNewLogoDesign
                                    size="xxsmall"
                                />
                                <span>
                                    <FormattedMessage
                                        id="jiraPlus.createAndSearchWithRovo"
                                        defaultMessage="Easily create and search for knowledge with <em>Rovo</em>"
                                        values={{ em: listEmphasis }}
                                    />
                                </span>
                            </li>
                        </ul>
                        <Button
                            css={[buttonCss, twcButtonCss]}
                            href={twcSignupHref}
                            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                                e.preventDefault(); // wait for analytics event to fire before navigating
                                createAnalyticsEvent({
                                    action: "clicked",
                                    actionSubject: "button",
                                    actionSubjectId: "getTwc",
                                    attributes: {
                                        source: "jiraPlusModal"
                                    }
                                }).fire();
                                window.location.assign(twcSignupHref);
                            }}
                        >
                            <FormattedMessage
                                id="jiraPlus.twcButtonText"
                                defaultMessage="Try them for free"
                            />
                        </Button>
                    </article>
                </section>
            </Modal>
        </JiraBackground>
    );
};

const headingEmphasis = (children: ReactNode) => (
    <span css={headingEmphasisCss}>{children}</span>
);

const listEmphasis = (children: ReactNode) => (
    <span css={listEmphasisCss}>{children}</span>
);

const COLORS = {
    // eslint-disable-next-line local/ensure-wac-token-usage
    B700: "#1868DB",
    // eslint-disable-next-line local/ensure-wac-token-usage
    B800: "#1558BC",
    // eslint-disable-next-line local/ensure-wac-token-usage
    B900: "#123263",
    // eslint-disable-next-line local/ensure-wac-token-usage
    N300: "#DDDEE1",
    // eslint-disable-next-line local/ensure-wac-token-usage
    N300A: "#0B120E24",
    // eslint-disable-next-line local/ensure-wac-token-usage
    P500: "#BF63F3"
};

const buttonCss = css({
    fontFamily: "inherit",
    fontSize: "0.875rem",
    fontWeight: 500,
    marginBlockStart: "auto",
    maxWidth: "auto",
    paddingBlock: "0.5rem",
    width: "100%"
});

const choiceCss = css({
    alignItems: "start",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: "2rem"
});

const descriptionCss = css({
    margin: 0
});

const headingCss = css({
    lineHeight: 1.18
});

const headingEmphasisCss = css({
    position: "relative",
    whiteSpace: "nowrap",
    zIndex: 0, // keeps text in front of background
    ["::before"]: {
        content: "",
        position: "absolute",
        backgroundColor: COLORS.P500,
        marginInlineStart: "-0.25rem",
        marginBlockStart: "0.125rem",
        width: "calc(100% + 0.5rem)",
        height: "calc(100% - 0.25rem)",
        transform: "rotate(-1.65deg)",
        zIndex: -1
    }
});

const iconsCss = css({
    display: "flex",
    gap: "0.5rem"
});

const jiraButtonCss = css({
    backgroundColor: "transparent",
    border: `1px solid ${COLORS.N300A}`,
    color: "inherit",
    ["&:hover"]: {
        backgroundColor: COLORS.N300
    }
});

const jiraChoiceCss = css({
    backgroundColor: "white",
    color: "inherit",
    [media.above.sm]: {
        paddingInlineEnd: "0.75rem"
    }
});

const listEmphasisCss = css({
    fontWeight: 700
});

const jiraListItemCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    svg: {
        color: COLORS.B700
    }
});

const listCss = css({
    listStyleType: "none",
    marginBlockEnd: "1.5rem",
    marginBlockStart: 0,
    padding: 0
});

const listIntroCss = css({
    fontWeight: 500,
    marginBlockEnd: 0,
    marginBlockStart: "1.5rem"
});

const listItemCss = css({
    alignItems: "start",
    display: "flex",
    gap: "0.75rem",
    marginBlockStart: "1.5rem",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    svg: {
        marginBlockStart: "0.125rem"
    }
});

const logosCss = css({
    alignItems: "center",
    display: "flex",
    gap: "1rem",
    marginBlockEnd: "1rem"
});

const modalContentCss = css({
    fontFamily: "var(--atlassian-sans-font-family)",
    [media.above.sm]: {
        display: "flex",
        maxWidth: "60.5rem",
        minHeight: "35rem",
        width: "100%"
    }
});

const separatorCss = css({
    display: "none",
    [media.above.sm]: {
        backgroundColor: COLORS.B900,
        clipPath: "polygon(100% 0, 0% 100%, 100% 100%)",
        display: "block",
        width: "2.5rem"
    }
});

const twcButtonCss = css({
    ["&:hover"]: {
        backgroundColor: COLORS.B800
    }
});

const twcChoiceCss = css({
    backgroundColor: COLORS.B900,
    color: "white",
    [media.above.sm]: {
        paddingInlineStart: "0.75rem"
    }
});

const twcLogosCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    svg: {
        "--text-color": "white"
    }
});
